/**
 * Cookie utility functions and in-memory access token store.
 *
 * Security: The access token is stored in memory only (not in a cookie, not in
 * localStorage/sessionStorage). This prevents XSS from reading the token via
 * document.cookie or a storage dump. Token lifetime is kept short; MSAL
 * silent-refresh (internal users) or the httpOnly refresh-token cookie
 * (external/OTP users, MDRS-556) is used to obtain new tokens when needed.
 */
import {
  LEGACY_APP_SESSION_STORAGE_KEY,
  TOKEN_STALE_THRESHOLD,
} from "../constants/auth";

/** In-memory access token (not in cookie to avoid XSS exposure). */
let inMemoryAccessToken: string | null = null;
/** Timestamp when the current token was set (for staleness checks). */
let tokenSetAt: number | null = null;

/** Callbacks to notify when another tab clears the app-issued session. */
const _externalSessionClearedListeners = new Set<() => void>();

// Deliberately NOT named with "auth"/"login"/"msal." in it — see
// clearAuthRelatedLocalStorage()'s own exemption below for why that alone
// isn't relied on either.
const EXPLICIT_LOGOUT_KEY = "medirect.session-ended";

// A `storage` event only fires in OTHER tabs than the one that made the
// change (that's the platform spec). EXPLICIT_LOGOUT_KEY is the shared,
// always-live-in-localStorage signal both auth paths already write on a real
// logout (markExplicitLogout(), called unconditionally by useAuth.ts's
// logout()) — MDRS-556 retargeted this listener onto it (previously it
// listened on the app token's own localStorage mirror, which no longer
// exists now that the token is in-memory only, like MSAL's). Only a SET is a
// logout signal; clearExplicitLogoutMarker() (a deliberate new login)
// removes the key and must NOT be treated as "someone logged out".
if (typeof window !== "undefined") {
  try {
    localStorage.removeItem(LEGACY_APP_SESSION_STORAGE_KEY);
  } catch {
    /* localStorage unavailable */
  }

  window.addEventListener("storage", (event) => {
    if (event.key !== EXPLICIT_LOGOUT_KEY || !event.newValue) return;
    inMemoryAccessToken = null;
    tokenSetAt = null;
    _externalSessionClearedListeners.forEach((callback) => callback());
  });
}

/**
 * Subscribe to another tab clearing the app-issued (email-code) session.
 * Returns an unsubscribe function. MSAL sessions are covered separately by
 * onExplicitLogoutElsewhere() below — this exists because the app-issued
 * token lives outside MSAL's cache entirely.
 */
export const onExternalSessionClearedElsewhere = (
  callback: () => void
): (() => void) => {
  _externalSessionClearedListeners.add(callback);
  return () => _externalSessionClearedListeners.delete(callback);
};

// When the last deliberate logout in this browser happened. Unlike
// EXPLICIT_LOGOUT_KEY, a new login never clears it (MDRS-556).
const LAST_LOGOUT_AT_KEY = "medirect.last-session-end";

// sessionStorage: when this tab's own MSAL session began. Browsers restore a
// tab's sessionStorage on reopen-closed-tab and copy it on duplicate, so this
// travels with the cached account it describes.
const TAB_SESSION_STARTED_AT_KEY = "medirect.tab-session-started";

// Not a credential: decides whether a failed ssoSilent may redirect to
// Microsoft's sign-in, which email-code users can't use.
const LAST_SIGN_IN_METHOD_KEY = "medirect.last-sign-in";

/** localStorage keys the auth cleanups below must never remove. */
const PRESERVED_KEYS = new Set([
  EXPLICIT_LOGOUT_KEY,
  LAST_LOGOUT_AT_KEY,
  LAST_SIGN_IN_METHOD_KEY,
]);

const _explicitLogoutListeners = new Set<() => void>();

// The tab-scoped MSAL cache (MDRS-556) gets no cross-tab ACCOUNT_REMOVED, so
// the explicit-logout marker is the live signal. Only a set counts: removal
// is a new sign-in (clearExplicitLogoutMarker).
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== EXPLICIT_LOGOUT_KEY || !event.newValue) return;
    _explicitLogoutListeners.forEach((callback) => callback());
  });
}

/** Subscribe to another tab logging out. Returns an unsubscribe function. */
export const onExplicitLogoutElsewhere = (callback: () => void): (() => void) => {
  _explicitLogoutListeners.add(callback);
  return () => _explicitLogoutListeners.delete(callback);
};

/**
 * Marks that the user deliberately clicked Log Out (useAuth.ts's `logout()`
 * only — never a defensive/automatic logout). Shared via localStorage so
 * every tab sees it. Read by index.jsx's bootstrap to skip its "silent SSO
 * for new tabs" attempt: that bootstrap can't otherwise tell a genuinely new
 * tab (already logged in elsewhere, should auto-join) apart from a tab that
 * was just explicitly logged out (should NOT silently sign back in just
 * because Microsoft's own SSO session outlives our app-side logout).
 */
export const markExplicitLogout = (): void => {
  try {
    const now = String(Date.now());
    localStorage.setItem(EXPLICIT_LOGOUT_KEY, now);
    localStorage.setItem(LAST_LOGOUT_AT_KEY, now);
  } catch {
    /* localStorage unavailable */
  }
};

/** Clears the marker — call when the user deliberately starts a new login. */
export const clearExplicitLogoutMarker = (): void => {
  try {
    localStorage.removeItem(EXPLICIT_LOGOUT_KEY);
  } catch {
    /* localStorage unavailable */
  }
};

/** Whether the user explicitly logged out and hasn't logged back in since. */
export const wasExplicitlyLoggedOut = (): boolean => {
  try {
    return localStorage.getItem(EXPLICIT_LOGOUT_KEY) !== null;
  } catch {
    return false;
  }
};

/**
 * Records that this tab's MSAL session began now (a sign-in, not a refresh).
 * A sign-in happening now is always after the last logout, so the stamp is
 * kept later than it even if the wall clock matches it or has moved back.
 */
export const markTabSessionStarted = (): void => {
  let lastLogoutAt = 0;
  try {
    lastLogoutAt = Number(localStorage.getItem(LAST_LOGOUT_AT_KEY)) || 0;
  } catch {
    /* localStorage unavailable */
  }
  try {
    sessionStorage.setItem(
      TAB_SESSION_STARTED_AT_KEY,
      String(Math.max(Date.now(), lastLogoutAt + 1))
    );
  } catch {
    /* sessionStorage unavailable */
  }
};

export type SignInMethod = "microsoft" | "email-code";

export const markSignInMethod = (method: SignInMethod): void => {
  try {
    localStorage.setItem(LAST_SIGN_IN_METHOD_KEY, method);
  } catch {
    /* localStorage unavailable */
  }
};

export const getLastSignInMethod = (): SignInMethod | null => {
  try {
    const method = localStorage.getItem(LAST_SIGN_IN_METHOD_KEY);
    return method === "microsoft" || method === "email-code" ? method : null;
  } catch {
    return null;
  }
};

/**
 * Whether this tab's cached MSAL session must not be trusted: logged out since,
 * or begun before the last logout in this browser (a reopened or duplicated
 * tab, even after a later sign-in cleared the marker).
 */
export const hasTabSessionEnded = (): boolean => {
  if (wasExplicitlyLoggedOut()) return true;
  try {
    const lastLogoutAt = Number(localStorage.getItem(LAST_LOGOUT_AT_KEY));
    if (!lastLogoutAt) return false;
    const startedAt = Number(sessionStorage.getItem(TAB_SESSION_STARTED_AT_KEY));
    return !startedAt || startedAt <= lastLogoutAt;
  } catch {
    return false;
  }
};

/**
 * Clears MSAL's cached account/token entries from localStorage (MSAL's own
 * cache keys embed the authority host, e.g. "...-login.microsoftonline.com-
 * accesstoken-...", so matching "msal."/"login"/"auth"/"token" substrings
 * catches them). Explicitly exempts PRESERVED_KEYS — they happen to not
 * collide today given its name, but a future edit to these substrings could
 * silently start deleting it again, right after it was set, the instant
 * it's written. Don't rely on naming alone a second time.
 *
 * The single source of truth for this filter — useAuth.ts's logout() and
 * tokenManager.ts's logoutUser() both call this rather than each keeping
 * their own hand-maintained copy of the substring list.
 */
export const clearAuthRelatedLocalStorage = (): void => {
  try {
    Object.keys(localStorage).forEach((key) => {
      if (PRESERVED_KEYS.has(key)) return;
      if (
        key.startsWith("msal.") ||
        key.includes("login") ||
        key.includes("auth") ||
        key.includes("token")
      ) {
        localStorage.removeItem(key);
      }
    });
  } catch {
    /* localStorage unavailable */
  }
};

/**
 * Removes the MSAL cache an earlier build left in localStorage; nothing else
 * would ever remove those tokens. Matches MSAL's own key rules ("msal." prefix
 * or client id) plus account entries listed in "msal.account.keys".
 */
export const clearLegacyMsalLocalStorage = (clientId: string): void => {
  try {
    let accountKeys: unknown = [];
    try {
      accountKeys = JSON.parse(localStorage.getItem("msal.account.keys") ?? "[]");
    } catch {
      /* malformed index — the prefix/client-id match below still applies */
    }
    const listedAccountKeys = new Set(Array.isArray(accountKeys) ? accountKeys : []);
    const lowerClientId = (clientId || "").toLowerCase();

    Object.keys(localStorage).forEach((key) => {
      if (PRESERVED_KEYS.has(key)) return;
      if (
        key.startsWith("msal.") ||
        // MSAL lowercases its credential keys. Guarded: an empty client id
        // would match every key.
        (lowerClientId && key.toLowerCase().includes(lowerClientId)) ||
        listedAccountKeys.has(key)
      ) {
        localStorage.removeItem(key);
      }
    });
  } catch {
    /* localStorage unavailable */
  }
};

/**
 * Set a cookie with the given name, value, and options
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {Object} options - Cookie options (expires, path, secure, etc.)
 */
export const setCookie = (
  name: string,
  value: string,
  options: Record<string, unknown> = {}
) => {
  const defaultOptions = {
    path: "/",
    secure: import.meta.env.PROD,
    sameSite: "strict",
    maxAge: 3600, // 1 hour in seconds
  };

  const cookieOptions = { ...defaultOptions, ...options } as {
    path: string;
    secure: boolean;
    sameSite: string;
    maxAge?: number;
  };

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (cookieOptions.maxAge) {
    cookieString += `; max-age=${cookieOptions.maxAge}`;
  }

  if (cookieOptions.path) {
    cookieString += `; path=${cookieOptions.path}`;
  }

  if (cookieOptions.secure) {
    cookieString += "; secure";
  }

  if (cookieOptions.sameSite) {
    cookieString += `; samesite=${cookieOptions.sameSite}`;
  }

  document.cookie = cookieString;
};

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} - Cookie value or null if not found
 */
export const getCookie = (name: string): string | null => {
  const cookies = document.cookie.split(";");
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith(`${name}=`)) {
      return decodeURIComponent(cookie.substring(name.length + 1));
    }
  }
  return null;
};

/**
 * Delete a cookie by name
 * @param {string} name - Cookie name
 */
export const deleteCookie = (name: string) => {
  document.cookie = `${name}=; max-age=0; path=/`;
};

/**
 * Set the access token in memory only (no cookie, no localStorage/sessionStorage).
 * Keeps the token out of document.cookie and any Storage API so XSS cannot
 * read it. Short effective lifetime is enforced via TOKEN_STALE_THRESHOLD
 * (MSAL) or the token's own short exp claim (external/OTP, MDRS-556); MSAL
 * silent-refresh or the httpOnly refresh-token cookie supplies new tokens.
 * @param {string} token - Access token (JWT)
 */
export const setAccessTokenCookie = (token: string) => {
  inMemoryAccessToken = token;
  tokenSetAt = Date.now();
};

/**
 * Get the access token from memory.
 * @returns {string|null} - Access token or null if not found
 */
export const getAccessTokenCookie = (): string | null => {
  return inMemoryAccessToken;
};

/**
 * Check if the token needs refreshing (older than specified threshold)
 * @param {number} thresholdMinutes - Minutes threshold to consider token stale
 * @returns {boolean} - Whether token needs refreshing
 */
export const isTokenStale = (thresholdMinutes = TOKEN_STALE_THRESHOLD): boolean => {
  if (tokenSetAt == null) return true;
  const tokenAgeMinutes = (Date.now() - tokenSetAt) / (1000 * 60);
  return tokenAgeMinutes > thresholdMinutes;
};

/**
 * Check if an access token exists and is not expired
 * @returns {boolean} - Whether a valid token exists
 */
export const hasValidToken = (): boolean => {
  const token = getAccessTokenCookie();
  return !!token && !isTokenStale();
};

/**
 * Clear the in-memory access token (and any legacy cookies if present).
 */
export const deleteAccessTokenCookie = () => {
  inMemoryAccessToken = null;
  tokenSetAt = null;
  deleteCookie("access_token");
  deleteCookie("token_timestamp");
};
